import {
  isGlobalSessionExpired,
  isMaintenanceError,
  isSessionExpiredError,
} from '@api-client/api-client';
import { isNetworkError, isRetryLaterAnswer } from '@utils/errorDetection';
import { sanitizeError } from '@utils/sanitizeError';
import { logger } from '@utils/logger';
import { maybeProbe } from './maintenance';

/**
 * Global onError handler for the TanStack QueryCache.
 *
 * Surfaces query failures to logger so production failures (e.g. badge fetch
 * 500s) are no longer invisible. Filters out the noise the recovery flow is
 * already handling:
 *
 * - Session-expired errors: already handled by the session-recovery flow;
 *   logging them here would double-log every query during reauth.
 * - MaintenanceError: our own request-interceptor short-circuit during an
 *   active takeover. Every blocked query surfaces here, so without the
 *   filter we flood telemetry with synthetic errors the user already sees
 *   as a takeover, AND we'd re-trigger maybeProbe for our own short-circuit.
 *
 * Belt-and-suspenders with the axios response interceptor: this catches
 * errors from any queryFn that doesn't go through axios. The detector
 * debounces redundant fires, so the duplication is observably free.
 *
 * Status-less errors are only forwarded to maybeProbe('network-error') when
 * they look network-shaped (axios codes like ERR_NETWORK / ECONNABORTED, or
 * fetch-level TypeErrors with messages like "Failed to fetch"). Generic
 * Errors thrown by app logic, parse failures, or assertions inside a queryFn
 * are status-less but unrelated to network health, and must not flip users
 * into a maintenance takeover.
 *
 * A 503 the backend sends by design (`isRetryLaterAnswer`: a busy database, or a lock wait on the
 * notification config pages, answered with a busy code and `Retry-After`) is the server up and
 * answering. The backend logs the notification pages' busy answers as a WARNING and `DATABASE_BUSY`
 * at ERROR. Either way the server has already recorded it, so it is logged as a warning here (which
 * the logger writes in development only) and is never forwarded to `maybeProbe`.
 *
 * A query may additionally declare `meta.suppressProbeWhenDataPresent` to keep its background
 * failures out of the probe chain while it still holds data -- see `SuppressProbeMeta` below --
 * and `meta.searchKeySegment` to keep typed search text out of the log (`SearchKeyMeta`).
 *
 * Extracted from `src/index.tsx` so the routing logic is unit-testable
 * without booting QueryClient or the React tree. Tests at
 * `src/__tests__/queryCacheOnError.test.ts`.
 */
/**
 * Opt-out a query can declare via `meta` for its BACKGROUND failures only.
 *
 * A query that polls faster than the probe debounce turns one flaky moment into a run of
 * probe chains, and a failed favicon fetch inside any of them raises a full-screen connection
 * takeover -- over a page that is painted, populated and perfectly usable, because query-core
 * retains `state.data` through a failed refetch. Declaring this says: while this query still
 * holds data, its failures are not evidence about the health of the network.
 *
 * Scoped as narrowly as possible. It suppresses nothing when the query has NO data: that
 * failure blocks the user, which is precisely what the global net is for. And it downgrades
 * the log rather than dropping it, so the failures stay visible in telemetry.
 */
export interface SuppressProbeMeta {
  suppressProbeWhenDataPresent?: boolean;
}

/**
 * Declared via `meta` by a query whose key carries text someone typed (a member or attendee name
 * search): the index of that segment. The producer owns its key's shape, so it names the segment
 * and this handler never has to know any key factory.
 */
export interface SearchKeyMeta {
  searchKeySegment?: number;
}

/**
 * The query key with its declared search segment replaced by `'<redacted>'`, so a failed search
 * never prints what someone typed (logger.error reaches production). Every other segment is kept
 * for debugging.
 */
const redactQueryKeySearch = (
  queryKey: readonly unknown[],
  meta: Record<string, unknown> | undefined,
): readonly unknown[] => {
  const index = meta?.searchKeySegment;
  if (typeof index !== 'number') return queryKey;
  const segment = queryKey[index];
  if (typeof segment !== 'string' || segment === '') return queryKey;
  return queryKey.map((seg, i) => (i === index ? '<redacted>' : seg));
};

export const queryCacheOnError = (
  error: unknown,
  query: {
    queryKey: readonly unknown[];
    meta?: Record<string, unknown>;
    state?: { data?: unknown };
  },
): void => {
  if (isGlobalSessionExpired()) return;
  if (isSessionExpiredError(error)) return;
  if (isMaintenanceError(error)) return;

  const logged = {
    queryKey: redactQueryKeySearch(query.queryKey, query.meta),
    error: sanitizeError(error),
  };

  const isBackgroundFailureOverData =
    query.meta?.suppressProbeWhenDataPresent === true && query.state?.data !== undefined;

  if (isBackgroundFailureOverData) {
    logger.warn('[query-error] background refetch failed over cached data', logged);
    return;
  }

  // A 503 the backend sends by design (`isRetryLaterAnswer`, a busy database
  // or a lock wait on the notification config pages) is the server answering and having already
  // recorded it: a warning here, and never forwarded, as the axios response interceptor never
  // forwards it either.
  if (isRetryLaterAnswer(error)) {
    logger.warn('[query-error] the server was busy and asks to be tried again', logged);
    return;
  }

  logger.error('[query-error]', logged);
  const err = error as { response?: { status?: number }; status?: number };
  const status = err.response?.status ?? err.status;
  // 503 takes the tier-1 fast path (single-corroboration threshold).
  // See `THRESHOLD_BY_REASON` in `src/lib/maintenance/detector.ts` for
  // the rationale (RFC 9110 explicit "service unavailable" semantics).
  if (status === 503) {
    void maybeProbe('503');
  } else if (status && status >= 500) {
    void maybeProbe('5xx');
  } else if (!status && isNetworkError(error)) {
    void maybeProbe('network-error');
  }
};
